import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const INITIAL_CONFIG = {
  title: '',
  description: '',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  variant: 'PRIMARY'
};

const useConfirmModal = () => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(INITIAL_CONFIG);
  const resolverRef = useRef(null);

  const resolvePending = useCallback((value) => {
    if (!resolverRef.current) return;
    resolverRef.current(value);
    resolverRef.current = null;
  }, []);

  useEffect(() => () => resolvePending(false), [resolvePending]);

  const closeModal = useCallback(() => {
    if (loading) return;
    setVisible(false);
    setConfig(INITIAL_CONFIG);
    resolvePending(false);
  }, [loading, resolvePending]);

  const confirmAction = useCallback(() => {
    if (loading) return;
    setVisible(false);
    setConfig(INITIAL_CONFIG);
    resolvePending(true);
  }, [loading, resolvePending]);

  const confirm = useCallback((nextConfig = {}) => new Promise((resolve) => {
    resolverRef.current = resolve;
    setConfig({ ...INITIAL_CONFIG, ...nextConfig });
    setVisible(true);
  }), []);

  const modalProps = useMemo(() => ({
    visible,
    loading,
    ...config,
    onConfirm: confirmAction,
    onClose: closeModal
  }), [visible, loading, config, confirmAction, closeModal]);

  return {
    confirm,
    setLoading,
    modalProps
  };
};

export default useConfirmModal;
